import { Moment } from 'moment'

import { ConfigFilterItem, ConfigItem, DefaultFile, EmptyDefaultFile } from 'Core/interfaces'
import { StateProvince } from 'Core/utils/storeHelpers/types'

import { CompanyDetailsFormControls } from 'Portals/Internal/Pages/CustomerRegistrationRequest/Edit/Form/CompanyDetailsFormControls/types'
import { CustomerDetailsFormControls } from 'Portals/Internal/Pages/CustomerRegistrationRequest/Edit/Form/CustomerDetailsFormControls/types'
import { MainFormControls } from 'Portals/Internal/Pages/CustomerRegistrationRequest/Edit/Form/MainFormControls/types'

export enum CustomerRegistrationRequestFormFields {
	customerName = 'customerName',
	customerEmailAddress = 'customerEmailAddress',
	customerPhoneNumber = 'customerPhoneNumber',
	customerWebsite = 'customerWebsite',
	customerContactId = 'customerContactId',
	assignedToContactId = 'assignedToContactId'
}

export enum CustomerRegistrationRequestFilterFields {
	dateFrom = 'dateFrom',
	dateTo = 'dateTo',
	statusId = 'statusId',
	assignedTo = 'assignedTo'
}

export enum CustomerRegistrationRequestSearchFields {
	searchByCustomerName = 'searchByCustomerName',
	searchByRegistrationRequestId = 'searchByRegistrationRequestId'
}

export enum CustomerRegistrationRequestFields {
	id = 'id',
	status = 'status',
	customerName = 'customerName',
	customerEmailAddress = 'customerEmailAddress',
	customerPhoneNumber = 'customerPhoneNumber',
	customerWebsite = 'customerWebsite',
	customerContact = 'customerContact',
	assignedToContact = 'assignedToContact',
	createdBy = 'createdBy',
	lastUpdatedBy = 'lastUpdatedBy',
	createDate = 'createDate',
	lastUpdateDate = 'lastUpdateDate'
}

export enum CustomerRegistrationRequestStatus {
	requested = 'Requested',
	completed = 'Completed',
	updatedByCustomer = 'Updated By Customer'
}

export enum CustomerRegistrationRequestDocumentFields {
	id = 'id',
	documentType = 'documentType',
	documentName = 'documentName',
	uploadDate = 'uploadDate',
	isRequired = 'isRequired'
}

export enum CustomerRegistrationRequestDocumentFormFields {
	file = 'file'
}

export declare namespace CustomerRegistrationRequest {
	interface Contact {
		id: number
		firstName: string
		lastName: string
		emailAddress: string
		phoneNumber: string
	}

	interface CreatedBy {
		id: number
		firstName: string
		lastName: string
		name: string
	}

	interface Basic {
		[CustomerRegistrationRequestFields.id]: number
		[CustomerRegistrationRequestFields.status]: Entity
		[CustomerRegistrationRequestFields.customerName]: BasicString
		[CustomerRegistrationRequestFields.customerEmailAddress]: BasicString
		[CustomerRegistrationRequestFields.customerPhoneNumber]: BasicString
		[CustomerRegistrationRequestFields.customerWebsite]: BasicString
		[CustomerRegistrationRequestFields.customerContact]: Nullable<Contact>
		[CustomerRegistrationRequestFields.assignedToContact]: Nullable<Contact>
		[CustomerRegistrationRequestFields.createdBy]: Nullable<Entity>
		[CustomerRegistrationRequestFields.lastUpdatedBy]: Nullable<Entity>
		[CustomerRegistrationRequestFields.createDate]: BasicString
		[CustomerRegistrationRequestFields.lastUpdateDate]: BasicString
	}

	interface Detailed extends Basic {
		id: number
		status: Entity
		[CustomerDetailsFormControls.customerName]: BasicString
		[CustomerDetailsFormControls.customerEmailAddress]: BasicString
		[CustomerDetailsFormControls.customerPhoneNumber]: BasicString
		[CustomerDetailsFormControls.customerWebsite]: BasicString
		[CustomerDetailsFormControls.customerContact]: Contact
		assignedToContact: Contact
		createdBy: CreatedBy
		createDate: BasicString
		lastUpdatedBy: CreatedBy
		lastUpdateDate: BasicString
		[CompanyDetailsFormControls.parentCompany]: BasicString
		[CompanyDetailsFormControls.ownershipType]: Entity
		[CompanyDetailsFormControls.doingBusinessAs]: BasicString
		[CompanyDetailsFormControls.yearBusinessEstablished]: BasicNumber
		[CompanyDetailsFormControls.principalName]: BasicString
		[CompanyDetailsFormControls.stateProvince]: StateProvince
		[CompanyDetailsFormControls.companyWebsiteAddress]: BasicString
		[CompanyDetailsFormControls.dunsNumber]: BasicString
		[CompanyDetailsFormControls.federalTaxId]: BasicString
		[CompanyDetailsFormControls.naicsCode]: BasicString
		[CompanyDetailsFormControls.companyPartyToAnyClaimOrLawsuit]: boolean
		[CompanyDetailsFormControls.taxLinesAgainstCompany]: boolean
		[CompanyDetailsFormControls.companyFiledBankruptcy]: boolean
		[CompanyDetailsFormControls.bankruptcyType]: Entity
		[CompanyDetailsFormControls.dateFilingBankruptcy]: BasicString
		submitCount: number
	}

	namespace Search {
		interface Config {
			[CustomerRegistrationRequestSearchFields.searchByCustomerName]: ConfigItem<
				CustomerRegistrationRequestSearchFields,
				Entity
			>
			[CustomerRegistrationRequestSearchFields.searchByRegistrationRequestId]: ConfigItem<
				CustomerRegistrationRequestSearchFields,
				Entity
			>
		}

		interface Result {
			[CustomerRegistrationRequestSearchFields.searchByCustomerName]: Entity[]
			[CustomerRegistrationRequestSearchFields.searchByRegistrationRequestId]: Entity[]
		}
	}

	namespace Filter {
		interface Config {
			[CustomerRegistrationRequestFilterFields.dateFrom]: ConfigFilterItem<string, null>
			[CustomerRegistrationRequestFilterFields.dateTo]: ConfigFilterItem<string, null>
			[CustomerRegistrationRequestFilterFields.statusId]: ConfigFilterItem<string, string>
			[CustomerRegistrationRequestFilterFields.assignedTo]: ConfigFilterItem<string, string>
		}
	}
	namespace Form {
		interface Add {
			[CustomerRegistrationRequestFormFields.customerName]: string
			[CustomerRegistrationRequestFormFields.customerEmailAddress]: string
			[CustomerRegistrationRequestFormFields.customerPhoneNumber]: string
			[CustomerRegistrationRequestFormFields.customerWebsite]: string
			[CustomerRegistrationRequestFormFields.customerContactId]: LabelValue<string>
			[CustomerRegistrationRequestFormFields.assignedToContactId]: string
		}
		interface Edit {
			isRequiredSchema: boolean
			[MainFormControls.assignee]: LabelValue<number> | null
			[MainFormControls.createdBy]: BasicString
			[MainFormControls.lastEditedBy]: BasicString
			[MainFormControls.createDate]: BasicString
			[MainFormControls.dateLastUpdated]: BasicString
			[CustomerDetailsFormControls.customerName]: BasicString
			[CustomerDetailsFormControls.customerPhoneNumber]: BasicString
			[CustomerDetailsFormControls.customerEmailAddress]: BasicString
			[CustomerDetailsFormControls.customerWebsite]: BasicString
			[CustomerDetailsFormControls.customerContact]: LabelValue<number> | null
			[CustomerDetailsFormControls.customerContactEmailAddress]: string | null
			[CustomerDetailsFormControls.customerContactPhoneNumber]: string | null
			[CompanyDetailsFormControls.parentCompany]: BasicString
			[CompanyDetailsFormControls.ownershipType]: LabelValue<number> | null
			[CompanyDetailsFormControls.doingBusinessAs]: BasicString
			[CompanyDetailsFormControls.yearBusinessEstablished]: Moment | null
			[CompanyDetailsFormControls.principalName]: BasicString
			[CompanyDetailsFormControls.stateProvince]: LabelValue | null
			[CompanyDetailsFormControls.companyWebsiteAddress]: BasicString
			[CompanyDetailsFormControls.dunsNumber]: BasicString
			[CompanyDetailsFormControls.federalTaxId]: BasicString
			[CompanyDetailsFormControls.naicsCode]: BasicString
			[CompanyDetailsFormControls.companyPartyToAnyClaimOrLawsuit]: boolean
			[CompanyDetailsFormControls.taxLinesAgainstCompany]: boolean
			[CompanyDetailsFormControls.companyFiledBankruptcy]: boolean
			[CompanyDetailsFormControls.dateFilingBankruptcy]: Moment | null
			[CompanyDetailsFormControls.bankruptcyType]: LabelValue<number> | null
		}

		interface Complete {
			companyReadonly: BasicString
			serviceLocations: number[]
		}
	}
	namespace Request {
		interface Add {
			[CustomerRegistrationRequestFormFields.customerName]: string
			[CustomerRegistrationRequestFormFields.customerEmailAddress]: string
			[CustomerRegistrationRequestFormFields.customerPhoneNumber]: string
			[CustomerRegistrationRequestFormFields.customerWebsite]: string
			[CustomerRegistrationRequestFormFields.customerContactId]: number
			[CustomerRegistrationRequestFormFields.assignedToContactId]: number
		}
		interface Edit {
			assignedToContactId: number
			customerName: string
			customerEmailAddress: string
			customerPhoneNumber: string
			customerWebsite: string
			customerContactId: number
			customerRegistrationStatusId: BasicNumber
			parentCompany: BasicString
			ownershipTypeId: BasicNumber
			doingBusinessAs: BasicString
			yearBusinessEstablished: BasicNumber
			principalName: BasicString
			stateProvinceId: BasicNumber
			companyWebsiteAddress: BasicString
			dunsNumber: BasicString
			federalTaxId: BasicString
			naicsCode: BasicString
			companyPartyToAnyClaimOrLawsuit: boolean
			taxLinesAgainstCompany: boolean
			companyFiledBankruptcy: boolean
			bankruptcyTypeId: BasicNumber
			dateFilingBankruptcy: BasicString
		}
		interface Complete {
			customerRegistrationRequest: Request.Edit
			serviceLocations: number[]
		}
	}

	namespace Document {
		interface Basic {
			[CustomerRegistrationRequestDocumentFields.id]: number
			[CustomerRegistrationRequestDocumentFields.documentType]: Nullable<Entity>
			[CustomerRegistrationRequestDocumentFields.documentName]: BasicString
			[CustomerRegistrationRequestDocumentFields.uploadDate]: BasicString
			[CustomerRegistrationRequestDocumentFields.isRequired]: boolean
		}

		interface Form {
			[CustomerRegistrationRequestDocumentFormFields.file]: Nullable<
				DefaultFile | EmptyDefaultFile
			>
		}

		interface Request {
			[CustomerRegistrationRequestDocumentFormFields.file]: Nullable<File>
		}
	}

	namespace ServiceLocation {
		interface Basic {
			id: number
			name: BasicString
			brandName: BasicString
			addressLine1: BasicString
			addressLine2: BasicString
			country: Nullable<IdCodeName>
			stateProvince: Nullable<StateProvince & { countryId: number }>
			county: Nullable<Entity>
			city: BasicString
			zipCode: BasicString
			contactName: BasicString
			contactTitle: BasicString
			contactEmail: BasicString
			contactPhoneNumber: BasicString
		}

		namespace Form {
			interface Generic {
				name: BasicString
				brandName: BasicString
				addressLine1: BasicString
				addressLine2: BasicString
				countryId: BasicNumber
				stateProvinceId: BasicNumber
				city: BasicString
				zipCode: BasicString
				countyId: BasicNumber
				contactName: BasicString
				contactTitle: BasicString
				contactEmail: BasicString
				contactPhoneNumber: BasicString
			}
		}
		namespace Request {
			interface Generic {
				name: BasicString
				brandName: BasicString
				addressLine1: BasicString
				addressLine2: BasicString
				countryId: BasicNumber
				stateProvinceId: BasicNumber
				countyId: BasicNumber
				city: BasicString
				zipCode: BasicString
				contactName: BasicString
				contactTitle: BasicString
				contactEmail: BasicString
				contactPhoneNumber: BasicString
			}
		}
	}
}
